// useLayoutEditorToolbarState.js

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * useLayoutEditorToolbarState
 *
 * Owns the floating layout editor toolbar state, measurement, refs, and open/clear interactions.
 *
 * @param {object} params - Hook parameters.
 * @param {function|null} [params.dismissMenu=null] - Optional callback to dismiss the slot menu before opening toolbar controls.
 * @param {*} [params.openMenuState=null] - Slot menu state used to trigger toolbar width remeasurement.
 *
 * @returns {object} Toolbar state values, refs, setters, and interaction handlers.
 */
const useLayoutEditorToolbarState = ({
	dismissMenu = null,
	openMenuState = null,
}) => {
	const [toolbarAnchor, setToolbarAnchor] = useState({
		left: 0,
		rowKey: '',
		top: 0,
		rowWidth: 0,
		slotHeight: 0,
		slotTop: 0,
		x: 12,
		y: 0,
	});
	const [toolbarRowKey, setToolbarRowKey] = useState('');
	const [toolbarSlotKey, setToolbarSlotKey] = useState('');
	const [toolbarMeasureState, setToolbarMeasureState] = useState({
		height: 0,
		rowKey: '',
		width: 0,
	});
	const toolbarHeaderRef = useRef(null);
	const layoutListRef = useRef(null);
	const activeToolbarRenderRowKey = String(toolbarRowKey || '').trim();

	/**
	 * handleOpenToolbar
	 *
	 * Opens the floating toolbar for a container row and optional slot target.
	 *
	 * @param {string} rowKey - Target container row key.
	 * @param {string} slotKey - Optional target slot key within the row.
	 * @param {MouseEvent|PointerEvent|object} event - Triggering interaction event.
	 */
	const handleOpenToolbar = useCallback((rowKey, slotKey, event) => {
		const cleanedRowKey = String(rowKey || '').trim();
		const cleanedSlotKey = String(slotKey || '').trim();
		if (!cleanedRowKey) return;

		const currentElement = event?.currentTarget || null;
		const rowElement = currentElement?.classList?.contains('layout-editor-layout-container-root')
			? currentElement
			: currentElement?.closest?.('.layout-editor-layout-container-root');
		const slotElement = currentElement?.closest?.('.layout-editor-slot-shell-container');
		const rect = rowElement?.getBoundingClientRect?.();
		const listRect = layoutListRef.current?.getBoundingClientRect?.();
		let slotTop = 0;
		let slotHeight = 0;

		if (slotElement && rect) {
			const slotRect = slotElement.getBoundingClientRect();
			slotTop = Math.max(0, Math.round(slotRect.top - rect.top));
			slotHeight = Math.max(0, Math.round(slotRect.height));
		}

		setToolbarAnchor({
			left: rect && listRect ? rect.left - listRect.left : 0,
			rowKey: cleanedRowKey,
			top: rect && listRect ? rect.top - listRect.top : 0,
			rowWidth: rect?.width || 0,
			slotHeight,
			slotTop,
			x: rect?.width ? event.clientX - rect.left : 12,
			y: rect?.height ? event.clientY - rect.top : 0,
		});

		dismissMenu?.();
		setToolbarRowKey(cleanedRowKey);
		setToolbarSlotKey(cleanedSlotKey);
	}, [dismissMenu]);

	/**
	 * handleClearToolbar
	 *
	 * Clears the active toolbar selection and anchor.
	 *
	 * @param {string} rowKey - Optional row key guard to avoid clearing another active toolbar target.
	 */
	const handleClearToolbar = useCallback((rowKey) => {
		const cleanedRowKey = String(rowKey || '').trim();
		if (cleanedRowKey && toolbarRowKey !== cleanedRowKey) return;

		setToolbarAnchor({
			left: 0,
			rowKey: '',
			top: 0,
			rowWidth: 0,
			slotHeight: 0,
			slotTop: 0,
			x: 12,
			y: 0,
		});
		setToolbarRowKey('');
		setToolbarSlotKey('');
	}, [toolbarRowKey]);

	useLayoutEffect(() => {
		if (!activeToolbarRenderRowKey || !toolbarHeaderRef.current) return;

		const nextWidth = toolbarHeaderRef.current.offsetWidth || 0;
		const nextHeight = toolbarHeaderRef.current.offsetHeight || 0;
		setToolbarMeasureState((current) => {
			if (
				current.rowKey === activeToolbarRenderRowKey
				&& current.width === nextWidth
				&& current.height === nextHeight
			) {
				return current;
			}

			return {
				height: nextHeight,
				rowKey: activeToolbarRenderRowKey,
				width: nextWidth,
			};
		});
	}, [activeToolbarRenderRowKey, openMenuState, toolbarSlotKey]);

	useEffect(() => {
		/**
		 * handleDocumentMouseDown
		 *
		 * Handles document mouse down.
		 *
		 * @param {object} event - event.
		 */
		function handleDocumentMouseDown(event) {
			const eventTarget = event?.target;
			if (typeof eventTarget?.closest === 'function') {
				if (eventTarget.closest('.layout-editor-layout-container-root')) return;
				if (eventTarget.closest('.layout-editor-shared-toolbar-container')) return;
				if (eventTarget.closest('.layout-editor-inspector')) return;
				if (eventTarget.closest('.layout-editor-inspector-shell')) return;
				if (eventTarget.closest('.popup-container')) return;
			}

			handleClearToolbar();
		}

		document.addEventListener('mousedown', handleDocumentMouseDown);

		return () => {
			document.removeEventListener('mousedown', handleDocumentMouseDown);
		};
	}, [handleClearToolbar]);

	return {
		handleClearToolbar,
		handleOpenToolbar,
		layoutListRef,
		setToolbarRowKey,
		setToolbarSlotKey,
		toolbarAnchor,
		toolbarHeaderRef,
		toolbarRowKey,
		toolbarSlotKey,
		toolbarMeasureState,
	};
};

export default useLayoutEditorToolbarState;